import React, { useState, useEffect } from 'react';
import { Field, useFormState, useForm } from 'react-final-form';
import { useIntl } from 'react-intl';
import { TextField, FormLabel, Checkbox, FormControlLabel } from '@mui/material';
import FieldRangeSlider from '../../../components/FieldRangeSlider/FieldRangeSlider';
import css from './ApplicationForm.module.css';

const SLIDER_MIN = 0;
const SLIDER_MAX = 4;
const DEFAULT_SLIDER_LABELS = [
  'ApplicationForm.slidingScale.noExperience',
  '',
  'ApplicationForm.slidingScale.intermediate',
  '',
  'ApplicationForm.slidingScale.highlyExperienced',
];

const SlidingScaleGroupField = ({ 
  name, 
  label, 
  options, 
  required, 
  readOnly,
  sliderLabels = DEFAULT_SLIDER_LABELS,
  noneLabel = 'ApplicationForm.badHabitsNone',
  otherLabel = 'ApplicationForm.badHabitsOther'
}) => {
  const intl = useIntl();
  const formState = useFormState();
  const form = useForm();
  // Extract the field name from the nested structure
  const getFieldValue = (fieldSuffix) => {
    if (name.includes('[') && name.includes(']')) {
      // Handle nested field names like "yourPets[0].badHabits"
      const parts = name.split('.');
      const arrayPart = parts[0]; // "yourPets[0]"
      const fieldPart = parts[1]; // "badHabits"
      const arrayIndex = arrayPart.match(/\[(\d+)\]/)?.[1];
      const arrayName = arrayPart.split('[')[0]; // "yourPets"
      
      if (arrayIndex !== undefined && formState.values?.[arrayName]?.[arrayIndex]) {
        return formState.values[arrayName][arrayIndex][`${fieldPart}_${fieldSuffix}`] || false;
      }
    }
    // Fallback to direct field access
    return formState.values?.[`${name}_${fieldSuffix}`] || false;
  };
  
  const noneChecked = getFieldValue('none');
  const otherChecked = getFieldValue('other');

  // Handle "None" checkbox change
  const handleNoneChange = (checked) => {
    if (checked && form) {
      // Reset all other fields immediately when "None" is checked
      options.forEach(opt => {
        if (opt.value !== 'none') {
          const fieldName = `${name}_${opt.value}`;
          if (opt.value === 'other') {
            form.change(`${name}_${opt.value}`, false);
            form.change(`${name}_${opt.value}Text`, '');
          } else {
            form.change(`${name}_${opt.value}`, 0);
          }
        }
      });
    } else {
      console.log('❌ SlidingScaleGroupField: Reset condition not met:', { checked, hasForm: !!form });
    }
  };

  // Handle "Other" checkbox change
  const handleOtherChange = (checked) => {
    if (checked && form) {
      // Reset all other fields immediately when "Other" is checked
      options.forEach(opt => {
        if (opt.value !== 'other') {
          const fieldName = `${name}_${opt.value}`;
          if (opt.value === 'none') {
            form.change(`${name}_${opt.value}`, false);
          } else {
            form.change(`${name}_${opt.value}`, 0);
          }
        }
      });
      console.log('✅ SlidingScaleGroupField: All fields reset completed');
    }
  };

  const shouldShowSlidingScales = !noneChecked && !otherChecked;
  const shouldShowNone = !otherChecked;
  const shouldShowOther = !noneChecked;

  return (
    <div className={css.slidingScaleGroupField}>
      <FormLabel className={css.questionTitle} style={{ marginBottom: 12, display: 'block' }}>
        {intl.formatMessage({ id: label })}
        {required && <span className={css.required}> *</span>}
      </FormLabel>
      <div className={css.slidingScaleOptionsList}>
        {options.map(opt => {
          // Handle "None" option - checkbox on the right
          if (opt.value === 'none') {
            return (
              <div key={opt.value} style={{ display: shouldShowNone ? 'block' : 'none' }}>
                <Field
                  name={`${name}_${opt.value}`}
                  render={({ input, meta }) => (
                    <div className={css.slidingScaleNoneOptionRow}>
                    <FormLabel className={css.slidingScaleNoneOptionLabel}>
                      {intl.formatMessage({ id: opt.label })}
                    </FormLabel>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={input.value || false}
                          onChange={(e) => {
                            if (!readOnly) {
                              input.onChange(e.target.checked);
                              handleNoneChange(e.target.checked);
                            }
                          }}
                          disabled={readOnly}
                        />
                      }
                      label=""
                    />
                  </div>
                )}
              />
              </div>
            );
          }

          // Handle "Other" option - checkbox with conditional textarea
          if (opt.value === 'other') {
            return (
              <div key={opt.value} style={{ display: shouldShowOther ? 'block' : 'none' }}>
                <Field
                  name={`${name}_${opt.value}`}
                  render={({ input, meta }) => (
                    <div className={css.slidingScaleOtherOptionRow}>
                      <FormLabel className={css.slidingScaleOtherOptionLabel}>
                        {intl.formatMessage({ id: opt.label })}
                      </FormLabel>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={input.value || false}
                            onChange={(e) => {
                              if (!readOnly) {
                                input.onChange(e.target.checked);
                                handleOtherChange(e.target.checked);
                                if (!e.target.checked && form) {
                                  // Clear the textarea when unchecked
                                  form.change(`${name}_${opt.value}Text`, '');
                                }
                              }
                            }}
                            disabled={readOnly}
                          />
                        }
                        label=""
                      />
                      {otherChecked && !noneChecked && (
                        <Field
                          name={`${name}_${opt.value}Text`}
                          render={({ input }) => (
                            <TextField
                              {...input}
                              placeholder={intl.formatMessage({ id: 'ApplicationForm.slidingScale.otherPlaceholder' })}
                              variant="outlined"
                              size="small"
                              fullWidth
                              className={css.slidingScaleOtherInput}
                              InputProps={{
                                readOnly: readOnly,
                              }}
                              required={otherChecked}
                            />
                          )}
                        />
                      )}
                    </div>
                  )}
                />
              </div>
            );
          }

          // Handle regular sliding scale options
          if (shouldShowSlidingScales) {
            return (
              <div key={opt.value} className={css.slidingScaleOptionRow}>
                <FormLabel className={css.slidingScaleOptionLabel}>
                  {intl.formatMessage({ id: opt.label })}
                </FormLabel>
                <Field
                  name={`${name}_${opt.value}`}
                  defaultValue={0}
                  render={({ input, meta }) => (
                    <div className={css.slidingScaleSlider}>
                      <FieldRangeSlider
                        id={`${name}_${opt.value}`}
                        name={`${name}_${opt.value}`}
                        min={SLIDER_MIN}
                        max={SLIDER_MAX}
                        step={1}
                        handles={[input.value ?? 0]}
                        onChange={val => {
                          if (!readOnly) {
                            input.onChange(Array.isArray(val) ? val[0] : val);
                          }
                        }}
                        rootClassName={css.slidingScaleSlider}
                        readOnly={readOnly}
                      />
                      <div className={css.slidingScaleLabels}>
                        {sliderLabels.map((l, i) => {
                          if (!l) {
                            return <span key={i} style={{ flex: 1 }} />;
                          }
                          return (
                            <span
                              key={l}
                              className={css.slidingScaleValue}
                              onClick={() => {
                                if (!readOnly) {
                                  input.onChange(i);
                                }
                              }}
                              onMouseEnter={(e) => {
                                if (!readOnly) {
                                  e.target.style.backgroundColor = '#e0e0e0';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!readOnly) {
                                  e.target.style.backgroundColor = 'transparent';
                                }
                              }}
                            >
                              {intl.formatMessage({ id: l })}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                />
              </div>
            );
          }

          return null;
        })}
      </div>
    </div>
  );
};

export default SlidingScaleGroupField;